import React, { useState, useRef, useEffect, useCallback } from 'react';
import { claimAPI, lookupAPI } from '../services/api.ts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MobileSelect from '../components/MobileSelect.tsx';
import MobileDatePicker from '../components/MobileDatePicker.tsx';

interface ClaimItem {
  id: number;
  amount: number;
  submitDate: string;
  remarks: string;
  workFlowId: number;
  status: string;
  createdDateTime: string;
  approvalDateTime?: string;
  approvalBy?: string;
  receiptUrl?: string;
  claimDetails?: ClaimDetailItem[];
}

interface ClaimDetailItem {
  id?: number;
  code: string;
  item: string; // Description/Item name
  transactionDate: string;
  description: string;
  amount: number;
  receiptFile?: File | null;
  receiptPreview?: string | null;
}

// Swipeable Claim Item Component
interface SwipeableClaimItemProps {
  item: ClaimDetailItem;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  disabled: boolean;
  formatDate: (dateString: string) => string;
}

const SwipeableClaimItem: React.FC<SwipeableClaimItemProps> = ({ item, index, onEdit, onDelete, disabled, formatDate }) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const ACTION_WIDTH = 160; // Width of action buttons (80px each for edit + delete)

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const currentX = e.touches[0].clientX;
    const diff = touchStartX - currentX;
    
    // Only allow swiping left (negative diff)
    if (diff > 0) {
      setSwipeOffset(Math.min(diff, ACTION_WIDTH));
    } else {
      setSwipeOffset(0);
    }
  };

  const handleTouchEnd = () => {
    if (swipeOffset > ACTION_WIDTH / 2) {
      // Swipe more than half, reveal actions
      setSwipeOffset(ACTION_WIDTH);
    } else {
      // Swipe less than half, hide actions
      setSwipeOffset(0);
    }
    setTouchStartX(null);
  };

  const handleActionClick = (action: () => void) => {
    action();
    setSwipeOffset(0); // Hide actions after action
  };

  // Close swipe when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (itemRef.current && !itemRef.current.contains(e.target as Node)) {
        setSwipeOffset(0);
      }
    };
    
    if (swipeOffset > 0) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [swipeOffset]);

  return (
    <div
      ref={itemRef}
      className="relative overflow-hidden bg-white"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Main Content */}
      <div
        className="flex items-center transition-transform duration-200 ease-out"
        style={{ transform: `translateX(-${swipeOffset}px)` }}
      >
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-gray-500">#{index + 1}</span>
                <span className="text-sm font-semibold text-gray-900">{item.code || '-'}</span>
                {item.item && (
                  <span className="text-xs text-gray-600 truncate">{item.item}</span>
                )}
              </div>
              <div className="text-sm text-gray-700 mb-1 line-clamp-2">{item.description}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500">
                  {item.transactionDate ? formatDate(item.transactionDate) : '-'}
                </span>
                <span className="text-sm font-semibold text-primary">RM {item.amount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons (revealed on swipe) */}
      <div
        className="absolute right-0 top-0 bottom-0 flex items-center"
        style={{ width: `${ACTION_WIDTH}px`, transform: `translateX(${ACTION_WIDTH - swipeOffset}px)` }}
      >
        <button
          onClick={() => handleActionClick(onEdit)}
          disabled={disabled}
          className="flex-1 h-full bg-blue-500 text-white flex items-center justify-center disabled:opacity-50"
          aria-label="Edit item"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          onClick={() => handleActionClick(onDelete)}
          disabled={disabled}
          className="flex-1 h-full bg-red-500 text-white flex items-center justify-center disabled:opacity-50"
          aria-label="Delete item"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const Claims: React.FC = () => {
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showClaimDetailModal, setShowClaimDetailModal] = useState(false);
  const [showClaimItemModal, setShowClaimItemModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<ClaimItem | null>(null);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [claimData, setClaimData] = useState({
    remarks: '',
    companyId: '',
    franchiseeId: '',
  });
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
  const [franchiseeOptions, setFranchiseeOptions] = useState<{ value: string; label: string }[]>([]);
  const [claimItems, setClaimItems] = useState<ClaimDetailItem[]>([]);
  const [currentItem, setCurrentItem] = useState<ClaimDetailItem>({
    code: '',
    item: '',
    transactionDate: '',
    description: '',
    amount: 0,
    receiptFile: null,
    receiptPreview: null,
  });
  const [itemOptions, setItemOptions] = useState<{ value: string; label: string }[]>([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [isSubmittingClaim, setIsSubmittingClaim] = useState(false);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [ocrParsedData, setOcrParsedData] = useState<{ amount?: number; date?: string; merchant?: string; description?: string } | null>(null);
  const claimReceiptInputRef = useRef<HTMLInputElement>(null);
  
  // Claims history state - separate state for each tab
  const [claimsHistory, setClaimsHistory] = useState<ClaimItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;
  const hasInitialLoadRef = useRef(false); // Prevent double fetch in StrictMode
  const [tab, setTab] = useState<'pending' | 'approved'>('pending'); // Tab state for filtering claims
  const observerTarget = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false); // Prevent multiple simultaneous loads
  
  // Get status filter ID based on tab (1 = pending/rejected, 2 = approved)
  const getStatusFilterId = (currentTab: 'pending' | 'approved') => {
    return currentTab === 'pending' ? 1 : 2;
  };

  // Calculate total amount from claim items
  const calculateTotal = useCallback(() => {
    return claimItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  }, [claimItems]);

  // Load company options (WorkerType enum) when claim modal opens
  useEffect(() => {
    if (showClaimModal) {
      lookupAPI.getWorkerTypes()
        .then((workerTypes) => {
          const options = workerTypes.map((wt: any) => ({
            value: String(wt.id || wt.Id || ''),
            label: wt.name || wt.Name || ''
          }));
          setCompanyOptions(options);
        })
        .catch((error) => {
          console.error('Error fetching worker types (companies):', error);
          setCompanyOptions([]);
        });
    }
  }, [showClaimModal]);

  // Load franchisee options from LoginFranchisee when claim modal opens
  useEffect(() => {
    if (!showClaimModal) return;

    claimAPI.getMyFranchisees()
      .then((franchisees: Array<{ id?: number; Id?: number; name?: string; Name?: string }>) => {
        const options = franchisees.map((franchisee) => ({
          value: String(franchisee.id || franchisee.Id || ''),
          label: franchisee.name || franchisee.Name || `Franchisee ${franchisee.id || franchisee.Id || ''}`
        })).filter((opt) => opt.value);

        setFranchiseeOptions(options);
        setClaimData((prev) => ({
          ...prev,
          franchiseeId: options[0]?.value || ''
        }));
      })
      .catch((error) => {
        console.error('Error fetching franchisees:', error);
        setFranchiseeOptions([]);
        setClaimData((prev) => ({ ...prev, franchiseeId: '' }));
      });
  }, [showClaimModal]);

  // Load item options (lookup codes) from LookupCode where LookupTypeId = 7
  useEffect(() => {
    if (showClaimItemModal) {
      // Fetch lookup codes for claim items (LookupTypeId = 7)
      lookupAPI.getLookupByTypeId(7)
        .then((response: any) => {
          // Handle response structure: response.data might be { data: [...] } or directly [...]
          const dataArray = Array.isArray(response.data) 
            ? response.data 
            : (response.data?.data || []);
          
          const options = dataArray.map((item: any) => {
            const code = item.value || item.Value || item.id || item.Id || '';
            const name = item.name || item.Name || '';
            const codeStr = String(code);
            
            // Format label as "name - code" (matching database format)
            // If name already contains the code, use name as-is, otherwise append code
            let label = '';
            if (name) {
              // Check if name already ends with the code
              if (name.trim().endsWith(`- ${codeStr}`) || name.trim().endsWith(codeStr)) {
                label = name.trim();
              } else {
                label = `${name.trim()} - ${codeStr}`;
              }
            } else {
              label = codeStr;
            }
            
            return {
              value: codeStr,
              label: label.trim()
            };
          });
          console.log(`[Claims] Loaded ${options.length} item options from lookup (LookupTypeId = 7)`);
          setItemOptions(options);
        })
        .catch((error) => {
          console.error('Error fetching lookup codes:', error);
          setItemOptions([]);
          toast.error('Failed to load items. Please try again.');
        });
    }
  }, [showClaimItemModal]);

  // Handle adding/editing claim item
  const handleSaveClaimItem = () => {
    if (!currentItem.transactionDate) {
      toast.error('Please select transaction date');
      return;
    }
    if (!currentItem.description || currentItem.description.trim() === '') {
      toast.error('Please enter description');
      return;
    }
    if (!currentItem.amount || currentItem.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (editingItemIndex !== null) {
      // Update existing item
      const updated = [...claimItems];
      updated[editingItemIndex] = { ...currentItem };
      setClaimItems(updated);
    } else {
      // Add new item
      setClaimItems([...claimItems, { ...currentItem }]);
    }

    // Reset form
    setCurrentItem({
      code: '',
      item: '',
      transactionDate: '',
      description: '',
      amount: 0,
      receiptFile: null,
      receiptPreview: null,
    });
    setEditingItemIndex(null);
    setIsProcessingOCR(false);
    setOcrParsedData(null);
    setShowClaimItemModal(false);
  };

  // Handle editing claim item
  const handleEditClaimItem = (index: number) => {
    const item = claimItems[index];
    setCurrentItem({ 
      ...item,
      receiptFile: item.receiptFile || null,
      receiptPreview: item.receiptPreview || null,
    });
    setEditingItemIndex(index);
    setIsProcessingOCR(false);
    setOcrParsedData(null);
    setShowClaimItemModal(true);
  };

  // Handle deleting claim item
  const handleDeleteClaimItem = (index: number) => {
    setClaimItems(claimItems.filter((_, i) => i !== index));
  };

  // Compress/resize image to reduce file size
  const compressImage = (file: File, maxWidth: number = 2000, maxHeight: number = 2000, quality: number = 0.85): Promise<File> => {
    return new Promise((resolve, reject) => {
      // Only compress image files
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      // If file is already small enough (< 800KB), don't compress
      if (file.size < 800 * 1024) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // Create a new File from the blob
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg', // Always use JPEG for better compression
                lastModified: Date.now(),
              });

              // If compressed file is still larger than original, use original
              if (compressedFile.size >= file.size) {
                resolve(file);
              } else {
                resolve(compressedFile);
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Handle receipt file selection and OCR processing (for current item)
  const handleReceiptChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const originalFile = e.target.files[0];
      
      // Compress image before processing
      let processedFile = originalFile;
      try {
        processedFile = await compressImage(originalFile);
        if (processedFile.size > 1024 * 1024) {
          toast.warning(`Image size is ${Math.round(processedFile.size / 1024)}KB. OCR may fail if size exceeds 1024KB.`);
        }
      } catch (error) {
        console.error('Error compressing image:', error);
        toast.warning('Failed to compress image, using original file');
      }

      const previewUrl = URL.createObjectURL(processedFile);
      
      setCurrentItem(prev => ({
        ...prev,
        receiptFile: processedFile,
        receiptPreview: previewUrl,
      }));
      setIsProcessingOCR(true);
      setOcrParsedData(null);

      // Call API to extract text from receipt using OCR
      try {
        const result = await claimAPI.extractReceiptText(processedFile);
        
        setIsProcessingOCR(false);
        setOcrParsedData(result.parsed || null);
        
        // Auto-populate current item fields with parsed data
        const updates: Partial<ClaimDetailItem> = {};
        
        if (result.parsed?.amount) {
          updates.amount = result.parsed.amount;
        }
        
        if (result.parsed?.date) {
          updates.transactionDate = new Date(result.parsed.date).toISOString().split('T')[0];
        }
        
        if (result.parsed?.merchant) {
          updates.item = result.parsed.merchant;
        }
        
        if (result.parsed?.description) {
          updates.description = result.parsed.description || result.parsed.merchant || 'Receipt item';
        } else if (result.parsed?.merchant) {
          updates.description = result.parsed.merchant;
        }
        
        setCurrentItem(prev => ({
          ...prev,
          ...updates,
        }));
        
        if (result.parsed?.amount) {
          toast.success(`Receipt extracted! Amount detected: RM ${result.parsed.amount.toFixed(2)}`);
        } else {
          toast.success('Receipt text extracted successfully');
        }
      } catch (error: any) {
        console.error('OCR Error:', error);
        setIsProcessingOCR(false);
        toast.error(error.message || 'Failed to extract text from receipt');
      }
    }
  };

  // Fetch claims history (initial load)
  const fetchClaimsHistory = async (page: number = 1, append: boolean = false, statusFilter?: number) => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current && !append) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingHistory(true);
      }
      
      // Use provided statusFilter or get from current tab
      const statusId = statusFilter !== undefined ? statusFilter : getStatusFilterId(tab);
      
      const response = await claimAPI.getClaims({ 
        PageNumber: page, 
        PageSize: PAGE_SIZE,
        StatusId: statusId
      });
      const newClaims = response.data || [];
      
      if (append) {
        setClaimsHistory(prev => {
          // Filter out duplicates by claim ID
          const existingIds = new Set(prev.map(c => c.id));
          const uniqueNewClaims = newClaims.filter(c => !existingIds.has(c.id));
          return [...prev, ...uniqueNewClaims];
        });
      } else {
        setClaimsHistory(newClaims);
      }
      
      setCurrentPage(response.currentPage || page);
      const totalPages = response.pageTotal || 1;
      setHasMore(page < totalPages);
    } catch (error: any) {
      console.error('Error fetching claims history:', error);
      toast.error('Failed to load claims history');
    } finally {
      setLoadingHistory(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
    }
  };

  // Load more claims (infinite scroll)
  const loadMoreClaims = useCallback(() => {
    if (!loadingMore && !loadingHistory && hasMore && hasInitialLoadRef.current && !isLoadingRef.current) {
      const nextPage = currentPage + 1;
      const statusId = getStatusFilterId(tab);
      fetchClaimsHistory(nextPage, true, statusId);
    }
  }, [loadingMore, loadingHistory, hasMore, currentPage, tab]);

  // Fetch claim details
  const fetchClaimDetails = async (id: number) => {
    try {
      const response = await claimAPI.getClaimById(id);
      setSelectedClaim(response.data);
      setShowClaimDetailModal(true);
    } catch (error: any) {
      console.error('Error fetching claim details:', error);
      toast.error('Failed to load claim details');
    }
  };

  // Load claims history on mount (prevent double fetch in StrictMode)
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      const statusId = getStatusFilterId(tab);
      fetchClaimsHistory(1, false, statusId);
    }
  }, []);

  // Reset and reload when tab changes (but not on initial mount)
  useEffect(() => {
    // Skip on initial mount - initial load is handled by the mount effect
    if (!hasInitialLoadRef.current) {
      return;
    }
    
    // Reset state and fetch new data for the selected tab
    setClaimsHistory([]);
    setCurrentPage(1);
    setHasMore(true);
    const statusId = getStatusFilterId(tab);
    fetchClaimsHistory(1, false, statusId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Infinite scroll observer
  useEffect(() => {
    // Don't set up observer until initial load is complete and we have some claims
    if (!hasInitialLoadRef.current || loadingHistory || claimsHistory.length === 0) {
      return;
    }

    // Disconnect previous observer if exists
    let observer: IntersectionObserver | null = null;

    // Add a small delay to prevent immediate triggering
    const timeoutId = setTimeout(() => {
      observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasMore && !loadingMore && !loadingHistory && hasInitialLoadRef.current && claimsHistory.length > 0) {
            loadMoreClaims();
          }
        },
        { threshold: 0.1, rootMargin: '200px' } // Increased rootMargin to prevent immediate triggering
      );

      const currentTarget = observerTarget.current;
      if (currentTarget) {
        observer.observe(currentTarget);
      }
    }, 300); // Small delay to ensure DOM is ready

    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        const currentTarget = observerTarget.current;
        if (currentTarget) {
          observer.unobserve(currentTarget);
        }
        observer.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadingHistory, loadMoreClaims, claimsHistory.length]);

  // Handle claim submission
  const handleSubmitClaim = async () => {
    if (claimItems.length === 0) {
      toast.error('Please add at least one claim item');
      return;
    }

    const totalAmount = calculateTotal();
    if (totalAmount <= 0) {
      toast.error('Total amount must be greater than zero');
      return;
    }

    if (franchiseeOptions.length > 1 && !claimData.franchiseeId) {
      toast.error('Please select a franchisee');
      return;
    }

    setIsSubmittingClaim(true);
    try {
      const formData = new FormData();
      formData.append('Amount', totalAmount.toString());
      formData.append('Remarks', claimData.remarks || '');
      if (claimData.companyId) {
        formData.append('CompanyId', claimData.companyId);
      }
      if (claimData.franchiseeId) {
        formData.append('FranchiseeId', claimData.franchiseeId);
      }

      // Use the first receipt file found from items (backend currently supports one receipt per claim)
      const firstReceiptFile = claimItems.find(item => item.receiptFile)?.receiptFile;
      if (firstReceiptFile) {
        formData.append('ReceiptFile', firstReceiptFile);
      }

      // Add claim details - format dates as ISO string for backend
      claimItems.forEach((item, index) => {
        // Format date as ISO datetime string for backend (DateTime type)
        // If transactionDate is just a date string (YYYY-MM-DD), convert to full datetime
        const transactionDate = item.transactionDate 
          ? (item.transactionDate.includes('T') 
              ? new Date(item.transactionDate).toISOString()
              : new Date(item.transactionDate + 'T00:00:00').toISOString())
          : new Date().toISOString();
        
        formData.append(`ClaimDetails[${index}].TransactionDate`, transactionDate);
        formData.append(`ClaimDetails[${index}].Description`, item.description);
        formData.append(`ClaimDetails[${index}].Amount`, item.amount.toString());
        if (item.code) {
          formData.append(`ClaimDetails[${index}].Code`, item.code);
        }
      });

      await claimAPI.createClaim(formData);
      
      toast.success('Claim submitted successfully!');
      setShowClaimModal(false);
      setClaimData({
        remarks: '',
        companyId: '',
        franchiseeId: '',
      });
      setClaimItems([]);
      
      // Refresh claims history
      setClaimsHistory([]);
      setCurrentPage(1);
      setHasMore(true);
      const statusId = getStatusFilterId(tab);
      fetchClaimsHistory(1, false, statusId);
    } catch (error: any) {
      console.error('Error submitting claim:', error);
      toast.error(error.message || 'Failed to submit claim. Please try again.');
    } finally {
      setIsSubmittingClaim(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-MY', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'transferred':
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // No need to filter client-side anymore - backend handles filtering
  const filteredClaims = claimsHistory;

  return (
    <div className="px-4 py-4">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header Section with Submit Button */}
      <section className="mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Claims</h2>
          <button
            onClick={() => setShowClaimModal(true)}
            className="bg-primary text-white px-3 py-2 rounded"
          >
            Submit Claim
          </button>
        </div>
      </section>

      {/* Tabs: Pending/Rejected vs Approved */}
      <div className="bg-white rounded border mb-3">
        <div className="flex text-sm">
          <button 
            onClick={() => {
              if (tab !== 'pending') {
                setTab('pending');
              }
            }} 
            className={`flex-1 py-2 ${tab === 'pending' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}
          >
            Pending/Rejected
          </button>
          <button 
            onClick={() => {
              if (tab !== 'approved') {
                setTab('approved');
              }
            }} 
            className={`flex-1 py-2 ${tab === 'approved' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}
          >
            Approved
          </button>
        </div>
        
        <div className="divide-y">
          {loadingHistory && claimsHistory.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">Loading claims...</span>
            </div>
          ) : filteredClaims.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>No requests yet.</p>
            </div>
          ) : (
            <>
              {filteredClaims.map((claim) => (
                <div
                  key={claim.id}
                  onClick={() => fetchClaimDetails(claim.id)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">Claim #{claim.id}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(claim.status)}`}>
                          {claim.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">RM {claim.amount.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>{formatDate(claim.submitDate)}</span>
                        </div>
                        {claim.remarks && (
                          <div className="text-xs text-gray-500 truncate">
                            {claim.remarks.replace(/Receipt:.*/g, '').trim().substring(0, 100)}
                            {claim.remarks.length > 100 && '...'}
                          </div>
                        )}
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
              
              {/* Infinite scroll trigger and loading indicator */}
              <div ref={observerTarget} className="py-4">
                {loadingMore && (
                  <div className="flex justify-center items-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading more...</span>
                  </div>
                )}
                {!hasMore && filteredClaims.length > 0 && (
                  <div className="text-center py-4 text-sm text-gray-500">
                    No more claims to load
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Claim Submission Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300 animate-fadeIn">
          <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden transform transition-transform duration-300 animate-slideUp max-h-[90vh] flex flex-col" style={{ marginBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
            {/* Header */}
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Submit Claim</h2>
              <button 
                onClick={() => {
                  setShowClaimModal(false);
                  setClaimData({
                    remarks: '',
                    companyId: '',
                    franchiseeId: '',
                  });
                  setClaimItems([]);
                  setCurrentItem({
                    code: '',
                    item: '',
                    transactionDate: '',
                    description: '',
                    amount: 0,
                    receiptFile: null,
                    receiptPreview: null,
                  });
                  setEditingItemIndex(null);
                  setIsProcessingOCR(false);
                  setOcrParsedData(null);
                }}
                className="absolute right-4 top-4 text-white"
                aria-label="Close modal"
                disabled={isSubmittingClaim}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Body */}
            <div className="p-4 overflow-y-auto flex-1">
              {/* Claim Items Section */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Claim Items <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentItem({
                        code: '',
                        item: '',
                        transactionDate: '',
                        description: '',
                        amount: 0,
                        receiptFile: null,
                        receiptPreview: null,
                      });
                      setEditingItemIndex(null);
                      setIsProcessingOCR(false);
                      setOcrParsedData(null);
                      setShowClaimItemModal(true);
                    }}
                    className="text-sm bg-primary text-white px-4 py-2 rounded"
                    disabled={isSubmittingClaim}
                  >
                    Add Item
                  </button>
                </div>

                {/* Claim Items List with Swipe Actions */}
                {claimItems.length === 0 ? (
                  <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-500 text-sm">
                    No items added. Click "Add Item" to add claim items.
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="divide-y divide-gray-200">
                      {claimItems.map((item, index) => (
                        <SwipeableClaimItem
                          key={index}
                          item={item}
                          index={index}
                          onEdit={() => handleEditClaimItem(index)}
                          onDelete={() => handleDeleteClaimItem(index)}
                          disabled={isSubmittingClaim}
                          formatDate={formatDate}
                        />
                      ))}
                    </div>
                    <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-t">
                      <span className="font-semibold text-gray-900">Total (RM):</span>
                      <span className="font-bold text-primary text-lg">RM {calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Company Selection */}
              <div className="mb-4">
                <MobileSelect
                  label="Company"
                  value={claimData.companyId}
                  options={companyOptions}
                  onChange={(value) => setClaimData(prev => ({ ...prev, companyId: value }))}
                  placeholder={companyOptions.length === 0 ? "Loading companies..." : "Select company"}
                />
              </div>

              {/* Franchisee Selection (only when user has multiple franchisees) */}
              {franchiseeOptions.length > 1 && (
                <div className="mb-4">
                  <MobileSelect
                    label="Franchisee"
                    value={claimData.franchiseeId}
                    options={franchiseeOptions}
                    onChange={(value) => setClaimData(prev => ({ ...prev, franchiseeId: value }))}
                    placeholder="Select franchisee"
                  />
                </div>
              )}

              {/* Remarks Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remarks
                </label>
                <textarea
                  value={claimData.remarks}
                  onChange={(e) => setClaimData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Additional notes (optional)"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isSubmittingClaim}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4" style={{ paddingBottom: 'max(1rem, calc(1rem + env(safe-area-inset-bottom, 0px)))' }}>
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={handleSubmitClaim}
                  disabled={isSubmittingClaim || claimItems.length === 0 || calculateTotal() <= 0}
                  className={`w-full py-3 rounded-md text-white font-medium text-center ${
                    isSubmittingClaim || claimItems.length === 0 || calculateTotal() <= 0
                      ? 'bg-gray-400'
                      : 'bg-primary hover:opacity-90'
                  }`}
                >
                  {isSubmittingClaim ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : 'Submit Claim'}
                </button>
                
                <button
                  onClick={() => {
                    setShowClaimModal(false);
                    setClaimData({
                      remarks: '',
                      companyId: '',
                      franchiseeId: '',
                    });
                    setClaimItems([]);
                    setCurrentItem({
                      code: '',
                      item: '',
                      transactionDate: '',
                      description: '',
                      amount: 0,
                      receiptFile: null,
                      receiptPreview: null,
                    });
                    setEditingItemIndex(null);
                    setIsProcessingOCR(false);
                    setOcrParsedData(null);
                  }}
                  className="w-full py-3 border border-gray-300 rounded-md text-gray-700 font-medium text-center bg-white"
                  disabled={isSubmittingClaim}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Item Modal (Add/Edit) */}
      {showClaimItemModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300 animate-fadeIn">
          <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden transform transition-transform duration-300 animate-slideUp max-h-[90vh] flex flex-col" style={{ marginBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
            {/* Header */}
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Claim Items</h2>
              <button 
                onClick={() => {
                  setShowClaimItemModal(false);
                  setCurrentItem({
                    code: '',
                    item: '',
                    transactionDate: '',
                    description: '',
                    amount: 0,
                  });
                  setEditingItemIndex(null);
                }}
                className="absolute right-4 top-4 text-white"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Body */}
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Item (Code/Item Name) */}
              {itemOptions.length > 0 && (
                <div>
                  <MobileSelect
                    label="Item"
                    value={currentItem.code}
                    options={itemOptions}
                    onChange={(value) => {
                      const selected = itemOptions.find(o => o.value === value);
                      if (selected) {
                        // Extract name from label (format: "name - code")
                        // Remove the code part from the end to get just the name
                        const codeStr = selected.value;
                        let name = selected.label;
                        
                        // Remove " - code" from the end if it exists
                        if (name.endsWith(` - ${codeStr}`)) {
                          name = name.slice(0, -(codeStr.length + 3)).trim();
                        } else if (name.endsWith(`- ${codeStr}`)) {
                          name = name.slice(0, -(codeStr.length + 2)).trim();
                        } else if (name.endsWith(codeStr) && name.length > codeStr.length) {
                          // Handle case where code is at the end without separator
                          name = name.slice(0, -codeStr.length).trim();
                        }
                        
                        setCurrentItem(prev => ({
                          ...prev,
                          item: name || selected.label,
                          code: value || ''
                        }));
                      }
                    }}
                    placeholder="Select item"
                  />
                </div>
              )}

              {/* Receipt Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Receipt
                </label>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleReceiptChange}
                  ref={claimReceiptInputRef}
                  className="hidden"
                />
                
                <div 
                  className={`relative border-2 ${currentItem.receiptFile ? 'border-primary' : 'border-dashed border-gray-300'} rounded-lg overflow-hidden`}
                  onClick={() => claimReceiptInputRef.current?.click()}
                >
                  {currentItem.receiptPreview ? (
                    <div className="relative">
                      <img
                        src={currentItem.receiptPreview}
                        alt="Receipt Preview"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white px-2 py-1 text-xs">
                        {currentItem.receiptFile?.name} ({Math.round((currentItem.receiptFile?.size || 0) / 1024)} KB)
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentItem(prev => ({
                            ...prev,
                            receiptFile: null,
                            receiptPreview: null,
                          }));
                          if (claimReceiptInputRef.current) {
                            claimReceiptInputRef.current.value = '';
                          }
                          setIsProcessingOCR(false);
                          setOcrParsedData(null);
                        }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md text-red-500"
                        aria-label="Remove receipt"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                      <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-600 font-medium">Tap to upload receipt</p>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG, or HEIC</p>
                    </div>
                  )}
                </div>

                {/* OCR Processing Indicator */}
                {isProcessingOCR && (
                  <div className="mt-2 flex items-center text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-sm">Extracting text from receipt...</span>
                  </div>
                )}

                {/* Auto-detected fields (only show if data was extracted) */}
                {ocrParsedData && !isProcessingOCR && (
                  <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="text-xs font-semibold text-primary mb-2">Auto-detected from receipt:</p>
                    <div className="space-y-1">
                      {ocrParsedData.amount && (
                        <p className="text-xs text-gray-700">✓ Amount: RM {ocrParsedData.amount.toFixed(2)}</p>
                      )}
                      {ocrParsedData.merchant && (
                        <p className="text-xs text-gray-700">✓ Merchant: {ocrParsedData.merchant}</p>
                      )}
                      {ocrParsedData.date && (
                        <p className="text-xs text-gray-700">✓ Date: {ocrParsedData.date}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={currentItem.description}
                  onChange={(e) => setCurrentItem(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Transaction Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Date <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setOpenDatePicker(true)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-left bg-white"
                >
                  {currentItem.transactionDate 
                    ? new Date(currentItem.transactionDate).toLocaleDateString('en-MY')
                    : 'Select date'}
                </button>
                <MobileDatePicker
                  isOpen={openDatePicker}
                  onClose={() => setOpenDatePicker(false)}
                  selectedDate={currentItem.transactionDate ? new Date(currentItem.transactionDate) : null}
                  onSelectDate={(date) => {
                    if (date) {
                      const dateStr = date.toISOString().split('T')[0];
                      setCurrentItem(prev => ({ ...prev, transactionDate: dateStr }));
                    }
                    setOpenDatePicker(false);
                  }}
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (RM) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={currentItem.amount || ''}
                  onChange={(e) => setCurrentItem(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4" style={{ paddingBottom: 'max(1rem, calc(1rem + env(safe-area-inset-bottom, 0px)))' }}>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    setShowClaimItemModal(false);
                    setCurrentItem({
                      code: '',
                      item: '',
                      transactionDate: '',
                      description: '',
                      amount: 0,
                      receiptFile: null,
                      receiptPreview: null,
                    });
                    setEditingItemIndex(null);
                    setIsProcessingOCR(false);
                    setOcrParsedData(null);
                  }}
                  className="w-full py-3 border border-gray-300 rounded-md text-gray-700 font-medium text-center bg-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveClaimItem}
                  className="w-full py-3 rounded-md text-white font-medium text-center bg-primary hover:opacity-90"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Detail Modal */}
      {showClaimDetailModal && selectedClaim && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300 animate-fadeIn">
          <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden transform transition-transform duration-300 animate-slideUp max-h-[90vh] flex flex-col" style={{ marginBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
            {/* Header */}
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Claim Details</h2>
              <button 
                onClick={() => {
                  setShowClaimDetailModal(false);
                  setSelectedClaim(null);
                }}
                className="absolute right-4 top-4 text-white"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Body */}
            <div className="p-4 overflow-y-auto flex-1">
              {/* Claim ID and Status */}
              <div className="mb-4 pb-4 border-b">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Claim #{selectedClaim.id}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedClaim.status)}`}>
                    {selectedClaim.status}
                  </span>
                </div>
              </div>

              {/* Amount */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <p className="text-lg font-semibold text-gray-900">RM {selectedClaim.amount.toFixed(2)}</p>
              </div>

              {/* Submit Date */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Submit Date</label>
                <p className="text-gray-900">{formatDate(selectedClaim.submitDate)}</p>
              </div>

              {/* Created Date */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                <p className="text-gray-900">{formatDate(selectedClaim.createdDateTime)}</p>
              </div>

              {/* Approval Info */}
              {selectedClaim.approvalDateTime && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Approved</label>
                  <p className="text-gray-900">{formatDate(selectedClaim.approvalDateTime)}</p>
                  {selectedClaim.approvalBy && (
                    <p className="text-sm text-gray-600">By: {selectedClaim.approvalBy}</p>
                  )}
                </div>
              )}

              {/* Claim Items Table */}
              {selectedClaim.claimDetails && selectedClaim.claimDetails.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Claim Items</label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">No</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">Code</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">Date</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700">Description</th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-700">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {selectedClaim.claimDetails.map((item, index) => (
                            <tr key={item.id || index} className="hover:bg-gray-50">
                              <td className="px-2 py-2 text-gray-900">{index + 1}</td>
                              <td className="px-2 py-2 text-gray-900">{item.code || '-'}</td>
                              <td className="px-2 py-2 text-gray-900">
                                {item.transactionDate ? formatDate(item.transactionDate) : '-'}
                              </td>
                              <td className="px-2 py-2 text-gray-900">{item.description}</td>
                              <td className="px-2 py-2 text-right text-gray-900 font-medium">
                                RM {item.amount.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50">
                          <tr>
                            <td colSpan={4} className="px-2 py-2 text-right font-semibold text-gray-900">
                              Total (RM):
                            </td>
                            <td className="px-2 py-2 text-right font-bold text-primary">
                              RM {selectedClaim.claimDetails.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Remarks */}
              {selectedClaim.remarks && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedClaim.remarks.replace(/Receipt:.*/g, '').trim()}</p>
                </div>
              )}

              {/* Receipt Image */}
              {selectedClaim.receiptUrl && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Receipt</label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <img
                      src={selectedClaim.receiptUrl}
                      alt="Receipt"
                      className="w-full h-auto object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Receipt+Not+Found';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4" style={{ paddingBottom: 'max(1rem, calc(1rem + env(safe-area-inset-bottom, 0px)))' }}>
              <button
                onClick={() => {
                  setShowClaimDetailModal(false);
                  setSelectedClaim(null);
                }}
                className="w-full py-3 border border-gray-300 rounded-md text-gray-700 font-medium text-center bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Claims;
